import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, X } from "lucide-react";
import { ModuleCards } from "./ModuleCards";
import { DimensionsList } from "./DimensionsList";

const API = "/api";
const authHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const DEFAULT_DIMENSIONS = [
  { key: "comida", label: "Como foi a comida?", modules: [] },
  { key: "entrega", label: "Como foi a entrega?", modules: ["Cardapio", "Delivery"] },
  { key: "atendimento", label: "Como foi o atendimento?", modules: [] },
];

/** Identificação interna nova (dim1, dim2…) que não repete nenhuma existente —
 *  antes era "dim" + tamanho da lista e, depois de apagar uma, podia repetir e
 *  misturar as notas de duas perguntas no relatório. */
const novaChave = (dims) => {
  const usadas = new Set(dims.map((d) => d.key));
  let n = dims.length + 1;
  while (usadas.has(`dim${n}`)) n += 1;
  return `dim${n}`;
};

/**
 * Modal de configuração da Campanha de Avaliação. Layout coluna única,
 * scroll interno isolado (body trava enquanto aberto pra evitar rolar a
 * página por trás). Sem preview lateral — feedback do user: "não tem
 * sentido". Desde 26/10/2026 (dono): espera fixa de 30 min e convite escrito
 * pela CLEO — a loja escolhe só os módulos e as perguntas.
 */
export function ReviewCampaignConfigModal({ campaign, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [modules, setModules] = useState(initialModules(campaign));
  const [dimensions, setDimensions] = useState(initialDimensions(campaign));

  // Trava scroll do body enquanto o modal está aberto — evita rolar a
  // página por trás quando o usuário dá scroll dentro do modal e bate
  // no fim do conteúdo. Restaura o valor original no unmount.
  useEffect(() => {
    const prevBody = document.body.style.overflow;
    const prevHtml = document.documentElement.style.overflow;
    document.body.style.overflow = "hidden";
    document.documentElement.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevBody;
      document.documentElement.style.overflow = prevHtml;
    };
  }, []);

  // Esc fecha o modal.
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const handleSave = async () => {
    const validation = validate({ modules, dimensions });
    if (validation) { toast.error(validation); return; }
    setSaving(true);
    try {
      await axios.put(
        `${API}/campaigns/review`,
        { modules, dimensions: dimensions.map((d) => ({ ...d, label: d.label.trim() })) },
        authHeader(),
      );
      toast.success("Configuração salva");
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-4"
      onClick={onClose}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div
        className="bg-card rounded-2xl border border-amber-500/30 shadow-2xl w-full max-w-2xl max-h-[92vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Header onClose={onClose} />
        <div className="flex-1 overflow-y-auto overscroll-contain p-5 space-y-5">
          <ModuleCards
            value={modules}
            onToggle={(m) => setModules((cur) => cur.includes(m) ? cur.filter((x) => x !== m) : [...cur, m])}
          />
          <DimensionsList
            dimensions={dimensions}
            ativos={modules}
            onUpdate={(i, patch) => setDimensions(dimensions.map((d, idx) => (idx === i ? { ...d, ...patch } : d)))}
            onAdd={() => addItem(dimensions, setDimensions, 5, { key: novaChave(dimensions), label: "", modules: [] }, "perguntas")}
            onRemove={(i) => removeItem(dimensions, setDimensions, i, "pergunta")}
          />
        </div>
        <Footer saving={saving} onClose={onClose} onSave={handleSave} />
      </div>
    </div>
  );
}

function Header({ onClose }) {
  return (
    <div className="px-5 py-4 border-b flex items-center justify-between bg-gradient-to-r from-amber-500/10 to-yellow-500/5 shrink-0">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-500 flex items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5 text-white" strokeWidth={2.2} />
        </div>
        <div className="min-w-0">
          <h2 className="text-lg font-bold leading-tight">Avaliação de Pedidos</h2>
          <p className="text-[11px] text-muted-foreground">
            Em quais pedidos e o que perguntar
          </p>
        </div>
      </div>
      <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground hover:text-foreground shrink-0" title="Fechar (Esc)">
        <X className="w-5 h-5" />
      </button>
    </div>
  );
}

function Footer({ saving, onClose, onSave }) {
  return (
    <div className="px-5 py-3.5 border-t bg-muted/30 flex items-center justify-end gap-2 shrink-0">
      <button
        type="button"
        onClick={onClose}
        className="px-4 py-2 rounded-xl bg-transparent hover:bg-muted text-muted-foreground hover:text-foreground font-semibold text-sm transition-colors"
      >
        Cancelar
      </button>
      <button
        type="button"
        onClick={onSave}
        disabled={saving}
        className="px-5 py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-semibold text-sm transition-colors disabled:opacity-50 shadow-md shadow-amber-500/30"
      >
        {saving ? "Salvando..." : "Salvar configuração"}
      </button>
    </div>
  );
}

function initialModules(c) {
  return Array.isArray(c?.modules) && c.modules.length ? c.modules : ["Cardapio", "Delivery"];
}
function initialDimensions(c) {
  return Array.isArray(c?.dimensions) && c.dimensions.length ? c.dimensions : DEFAULT_DIMENSIONS;
}
function validate({ modules, dimensions }) {
  if (modules.length === 0) return "Escolha ao menos um módulo";
  if (dimensions.some((d) => !d.key?.trim() || !d.label?.trim())) return "Escreva o texto de todas as perguntas";
  // Módulo ligado sem nenhuma pergunta: o cliente veria todas (inclusive as de outros módulos)
  const semPergunta = modules.filter((m) => !dimensions.some((d) => !(d.modules || []).length || d.modules.includes(m)));
  if (semPergunta.length) return "Todo módulo ligado precisa de ao menos uma pergunta (marque \"Todos\" numa delas)";
  return null;
}

function addItem(arr, setter, max, item, name) {
  if (arr.length >= max) { toast.error(`Máximo de ${max} ${name}`); return; }
  setter([...arr, item]);
}

function removeItem(arr, setter, idx, name) {
  if (arr.length <= 1) { toast.error(`Defina ao menos 1 ${name}`); return; }
  setter(arr.filter((_, i) => i !== idx));
}
